import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { createLoadingShimmerStyles } from '../styles/loader-shimmer';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

type ChartProps = {
  title: string;
  unit: string;
  data: Record<string, { color: string; number: number }>;
  loading: boolean;
};

const Chart = ({ title, unit, data, loading }: ChartProps) => {
  const theme = useTheme();
  const { t } = useTranslation('random');

  const processedData = Object.entries(data)
    .map(([label, data]) => ({
      count: data.number,
      color: data.color,
      label: label,
    }))
    .filter((item) => item.count > 0);

  const totalCount = processedData.reduce((sum, item) => sum + item.count, 0);

  const chartData = {
    labels: processedData.map((item) => item.label),
    datasets: [
      {
        data: processedData.map((item) => item.count),
        backgroundColor: processedData.map((item) => item.color),
        borderWidth: 0,
        cutout: '70%',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: theme.palette.background.paper,
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.primary,
        callbacks: {
          label: function (context: any) {
            const percentage = ((context.parsed / totalCount) * 100).toFixed(0);
            return `${context.parsed} ${unit} (${percentage}%)`;
          },
        },
      },
      datalabels: {
        display: true,
        color: theme.palette.neutral.white,
        font: {
          family: 'Poppins SemiBold, sans-serif',
          size: 12,
        },
        formatter: (value: number) => value,
      },
    },
    elements: {
      arc: {
        borderWidth: 0,
      },
    },
  };

  return (
    <ChartContainer loading={loading}>
      <LegendContainer>
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Poppins SemiBold, sans-serif',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '19.6px',
            textAlign: 'left',
          }}
        >
          {loading ? t('loadingText') : title}
        </Typography>
        <LegendColumn>
          {Object.entries(processedData).map(([key, value]) => {
            return (
              <LegendItem key={key}>
                <ColorDot color={value.color} />
                <LegendText>{value.label}</LegendText>
              </LegendItem>
            );
          })}
        </LegendColumn>
      </LegendContainer>
      <ChartSection>
        <ChartWrapper>
          <Doughnut data={chartData} options={options} />
        </ChartWrapper>
        <Typography
          sx={{
            fontFamily: 'Poppins SemiBold, sans-serif',
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: '19.6px',
          }}
        >
          {loading ? '' : `${totalCount} ${unit}`}
        </Typography>
      </ChartSection>
    </ChartContainer>
  );
};

export default Chart;
export { Chart };

// Styled components
const ChartContainer = styled(Box, { shouldForwardProp: (prop) => prop !== 'loading' })<{ loading: boolean }>(
  ({ theme, loading }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    backgroundColor: 'white',
    padding: '16px',
    borderRadius: '10px',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: '16px',
    },
    ...createLoadingShimmerStyles({ active: loading, contentOpacity: 0.6, speedMs: 1200, lightOpacity: 0.6 }),
  }),
);

const LegendContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: '14px',
});

const LegendColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

const LegendItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const ColorDot = styled('div')<{ color: string }>(({ color }) => ({
  width: '7px',
  height: '7px',
  borderRadius: '50%',
  backgroundColor: color,
}));

const LegendText = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral[80],
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '16.8px',
}));

const ChartSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
});

const ChartWrapper = styled(Box)({
  width: '150px',
});
